import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { FaCubes, FaLeaf } from "react-icons/fa";
import { useDeleteActivity } from "./useDeleteActivity";
import { useActivitiesPagination } from "./useActivitiesPagination";
import NewActivityTypeModal from "./NewActivityTypeModal";
import { selectCurrentActions } from "@features/builder/builderSlice";
import { useAppParams, useAppSelector, useWindowDimetions } from "@app/hooks";
import ActivityCard, { CreateNewActivityCard } from "./ActivityCard";
import ConfirmationModal from "@components/modal/ConfirmationModal";
import Pagination from "@components/pagination/Pagination";
import TranslatableText from "@components/translations/TranslatableText";
import Skeleton from "react-loading-skeleton";
import Masonry from "react-responsive-masonry";
import useErrorMessage from "@utils/useErrorMessage";
import FadeAnimation from "@components/animations/FadeAnimation";
import { Actions } from "@typedefs/interfaces";
import AlertModal from "@components/modal/AlertModal";
import ProjectContentHeader from "../ProjectContentHeader";
import { selectCurrentProject } from "@app/features/project/projectSlice";

const ITEMS_PER_PAGE = 10;

const ProjectActivitiesContainer = () => {
	const { projectId } = useAppParams();
	const { project } = useAppSelector(selectCurrentProject);
	const location = useLocation();
	const navigate = useNavigate();
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [isTypeModalOpen, setIsTypeModalOpen] = useState<boolean>(false);
	const { width } = useWindowDimetions();
	const isLargeScreen = width > 2560;
	const itemsPerPage = useMemo(() => isLargeScreen ? ITEMS_PER_PAGE * 2 : ITEMS_PER_PAGE, [isLargeScreen]);

	const {
		activities,
		activitiesCount,
		isLoading,
		isFetching,
		isError,
		error,
		handleNext,
		handlePrev,
		handlePageChange,
		refetch,
	} = useActivitiesPagination({
		projectId: projectId!,
		itemsPerPage,
	});

	const {
		isDeleteDialogOpen,
		handleCancel,
		handleDelete,
		showDeleteModal,
		isDeleteLoading,
		deleteErrorMsg,
		isDeleteError,
	} = useDeleteActivity(refetch);

	const { errorMsg } = useErrorMessage({ error, isError });
	const actions = useAppSelector(selectCurrentActions);
	const includeNotes = actions?.includes(Actions.Notes);

	const handleOpenModal = () => {
		setIsTypeModalOpen(true);
	};

	const handleCloseTypeModal = () => {
		setIsTypeModalOpen(false);
	};

	const handleSelectGHG = () => {
		handleCloseTypeModal();
		navigate("../new-activity");
	};

	const handleSelectBiodiversity = () => {
		handleCloseTypeModal();
		navigate("../new-biodiversity-activity");
	};

	const activityTypeCounts = useMemo(() => {
		if (!activities?.length) return { ghg: 0, biodiversity: 0 };
		return activities.reduce(
			(acc, act) => {
				acc[act.is_b_intact ? "biodiversity" : "ghg"] += 1;
				return acc;
			},
			{ ghg: 0, biodiversity: 0 } as { ghg: number; biodiversity: number },
		);
	}, [activities]);

	return projectId && projectId !== "new" ? (
		<section style={{ paddingBottom: 20 }}>
			<ConfirmationModal
				open={isDeleteDialogOpen}
				onCancel={handleCancel}
				onConfirm={handleDelete}
				message={<TranslatableText translationKey="activity.delete_confirmation" />}
				isLoading={isDeleteLoading}
			/>

			<AlertModal
				modalOpen={!!errorMessage}
				handleCancel={() => setErrorMessage(null)}
				children={
					<div className="d-flex p-2">
						<span className="error fs-13">
							{errorMessage}
						</span>
					</div>}
			/>

			<NewActivityTypeModal
				isOpen={isTypeModalOpen}
				onClose={handleCloseTypeModal}
				onSelectGHG={handleSelectGHG}
				onSelectBiodiversity={handleSelectBiodiversity}
			/>

			<div className="module-group">
				<ProjectContentHeader project={project} includeNotes={includeNotes} title="project.project_activities" />
			</div>

			{!isError && !isDeleteError && !isLoading && !isFetching && activities && activityTypeCounts.ghg > 0 && activityTypeCounts.biodiversity > 0 && (
				<div className="d-flex flex-wrap gap-8 pt-1" style={{ marginLeft: "90px" }}>
					<div
						className="d-flex align-items-center gap-8 px-2 py-1"
						style={{
							backgroundColor: "var(--bintact-light-green, #e8f5e9)",
							color: "var(--bintact-bright, #4caf50)",
						}}
					>
						<FaCubes size={14} color="var(--bintact-bright, #4caf50)" />
						<span className="fs-11 ff-medium">
							{activityTypeCounts.ghg} <TranslatableText translationKey="activity.ghg" />
						</span>
					</div>
					<div
						className="d-flex align-items-center gap-8 px-2 py-1"
						style={{
							backgroundColor: "var(--bintact-light, #fff3e0)",
							color: "#d97706",
						}}
					>
						<FaLeaf size={14} color="#d97706" />
						<span className="fs-11 ff-medium">
							{activityTypeCounts.biodiversity} <TranslatableText translationKey="activity.biodiversity" />
						</span>
					</div>
				</div>
			)}

			<div className="activity-cards-container">
				{isError ? (
					<>
						<span className="error fs-13 pb-2">
							{errorMsg
								? errorMsg
								: `An error occurred while fetching activities for project ${projectId}`}
						</span>
					</>
				) : isDeleteError ? (
					<>
						<span className="error fs-13 pb-2">
							{deleteErrorMsg
								? deleteErrorMsg
								: "An error occurred while deleting an activity"}
						</span>
					</>
				) : (
					<Masonry columnsCount={isLargeScreen ? 3 : 2} gutter="1rem">
						<FadeAnimation isShown>
							<CreateNewActivityCard onOpenModal={handleOpenModal} />
						</FadeAnimation>
						{isLoading || isFetching
							? Array(activities?.length ?? 3)
								.fill(0)
								.map((_, i) => (
									<FadeAnimation key={`cart-skeleton-${i}`} delay={i * 0.1} isShown>
										<Skeleton height={108} borderRadius={8} />
									</FadeAnimation>
								))
							: isDeleteLoading
								? activities?.map((act, i) => (
									<FadeAnimation key={act.id} delay={i * 0.1} isShown>
										<Skeleton height={108} borderRadius={8} />
									</FadeAnimation>
								))
								: activities?.length
									? activities.map((act, i) => (
										<FadeAnimation
											className="card-container"
											key={act.id}
											delay={i * 0.1} // 0.1s delay between each card
											isShown
										>
											<ActivityCard
												activity={act}
												onDelete={() => showDeleteModal(act.id)}
												onError={(error) => setErrorMessage(error)}
											/>
										</FadeAnimation>
									))
									: null}
					</Masonry>
				)}
			</div>

			{activities?.length === 0 ? (
				<footer className="module-group-footer">
					<div className="module-group">
						<span className="ff-light fs-12 pb-2">
							* No activities have been added yet. Please, add activities to the
							project to start the assessment.
						</span>
					</div>
				</footer>
			) : null}
			{
				activities && activitiesCount && activitiesCount > itemsPerPage ? (
					<Pagination itemsPerPage={itemsPerPage} totalItems={activitiesCount ?? 0} onNext={handleNext} onPrevious={handlePrev} onPageClick={handlePageChange} size="md" />
				) : null
			}
		</section>
	) : (
		<Navigate
			to={"/project/new/description"}
			state={{ from: location }}
			replace
		/>
	);
};

export default ProjectActivitiesContainer;
